// src/pages/AssignOrders.js
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  Table,
  Button,
  Tag,
  Space,
  Input,
  Select,
  Modal,
  Card,
  Typography,
  Tooltip,
  Skeleton,
  message,
} from "antd";
import { 
  EyeOutlined, 
  SearchOutlined, 
  UserAddOutlined,
  MailOutlined,
  PhoneOutlined,
  CreditCardOutlined,
  InfoCircleOutlined,
  EnvironmentOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import { Avatar } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { Row, Col } from "antd";
import { motion, AnimatePresence } from "framer-motion";
import BASEURL from "../API/Config";

const { Title, Text } = Typography;
const { Option } = Select;

const AssignOrders = () => {
  const [orders, setOrders] = useState([]);
  const [deliveryBoys, setDeliveryBoys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [assignedFilter, setAssignedFilter] = useState("");
  const [assignmentFilter, setAssignmentFilter] = useState("");

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedDeliveryBoy, setSelectedDeliveryBoy] = useState("");
  const [assigning, setAssigning] = useState(false);

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewOrder, setViewOrder] = useState(null);

  const ordersApi = `${BASEURL}/user/getallorders`;
  const deliveryBoyApi = `${BASEURL}/user/getdeliverboy`;
  const assignApi = `${BASEURL}/orders/orders/assign-delivery-boy`;

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          message.error("Authentication session expired. Please login again.");
          setLoading(false);
          return;
        }

        const [ordersRes, boysRes] = await Promise.all([
          axios.get(ordersApi, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(deliveryBoyApi, { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        setOrders((ordersRes.data || []).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
        setDeliveryBoys(boysRes.data || []);
      } catch (err) {
        console.error(err);
        message.error("Failed to fetch data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [ordersApi, deliveryBoyApi]);

  // Filtering logic
  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        order.user?.email?.toLowerCase().includes(term) ||
        order.shippingInfo?.address?.toLowerCase().includes(term) ||
        order.trackingNumber?.toLowerCase().includes(term) ||
        order.orderStatus?.toLowerCase().includes(term);

      const matchesStatus = statusFilter === "" || order.orderStatus === statusFilter;

      const assignedBoy = deliveryBoys.find((b) => b.AssignOrders.includes(order._id));
      const matchesAssigned =
        assignedFilter === "" || (assignedBoy && assignedBoy._id === assignedFilter);

      const matchesAssignment = 
        assignmentFilter === "" || 
        (assignmentFilter === "Assigned" ? !!assignedBoy : !assignedBoy);

      return matchesSearch && matchesStatus && matchesAssigned && matchesAssignment;
    });
  }, [orders, searchTerm, statusFilter, assignedFilter, assignmentFilter, deliveryBoys]);

  const handleAssignOrder = async () => {
    if (!selectedDeliveryBoy) {
      message.warning("Please select a delivery boy");
      return;
    }
    try {
      setAssigning(true);
      const token = localStorage.getItem("token");
      await axios.put(
        assignApi,
        { orderId: selectedOrderId, deliveryBoyId: selectedDeliveryBoy },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      message.success("Delivery boy assigned successfully!");
      setOrders((prev) =>
        prev.map((o) => (o._id === selectedOrderId ? { ...o, orderStatus: "Ready to Ship" } : o))
      );
      setDeliveryBoys((prev) =>
        prev.map((boy) =>
          boy._id === selectedDeliveryBoy
            ? { ...boy, AssignOrders: [...boy.AssignOrders, selectedOrderId] }
            : boy
        )
      );
      setShowAssignModal(false);
    } catch (err) {
      console.error(err);
      message.error("Failed to assign delivery boy.");
    } finally {
      setAssigning(false);
    }
  };

  const getStatusTag = (status) => {
    let color = "default";
    switch (status) {
      case "Delivered": color = "success"; break;
      case "Ordered": color = "processing"; break;
      case "Ready to Ship": color = "warning"; break;
      case "Out for Delivery": color = "magenta"; break;
      case "cancelled": color = "error"; break;
      default: color = "default";
    }
    return <Tag color={color} style={{ borderRadius: '6px', fontWeight: 600 }}>{status?.toUpperCase() || "N/A"}</Tag>;
  };

  const columns = [
    {
      title: "S.No",
      key: "index",
      render: (text, record, index) => index + 1,
      width: 70,
    },
    {
      title: "Customer",
      key: "customer",
      render: (record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.shippingInfo?.firstName} {record.shippingInfo?.lastName}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.user?.email}</Text>
        </Space>
      ),
    },
    {
      title: "Address",
      dataIndex: ["shippingInfo", "address"],
      key: "address",
      ellipsis: true,
      render: (text) => <Tooltip title={text}>{text || "N/A"}</Tooltip>,
    },
    {
      title: "Status",
      dataIndex: "orderStatus",
      key: "status",
      render: (status) => getStatusTag(status),
      filters: [
        { text: 'Ordered', value: 'Ordered' },
        { text: 'Ready to Ship', value: 'Ready to Ship' },
        { text: 'Out for Delivery', value: 'Out for Delivery' },
        { text: 'Delivered', value: 'Delivered' },
        { text: 'Cancelled', value: 'cancelled' },
      ],
      onFilter: (value, record) => record.orderStatus === value,
    },
    {
      title: "Tracking No",
      dataIndex: "trackingNumber",
      key: "tracking",
      render: (text) => <Text code>{text || "N/A"}</Text>,
    },
    {
      title: "Assigned To",
      key: "assigned",
      render: (record) => {
        const boy = deliveryBoys.find((b) => b.AssignOrders.includes(record._id));
        return boy ? (
          <Space>
            <Avatar size="small" style={{ backgroundColor: '#87d068' }} icon={<UserOutlined />} />
            <Text>{boy.firstname} {boy.lastname}</Text>
          </Space>
        ) : <Text type="secondary">Not Assigned</Text>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (record) => (
        <Space>
          <Tooltip title="View Details">
            <Button 
              shape="circle" 
              icon={<EyeOutlined />} 
              onClick={() => { setViewOrder(record); setShowViewModal(true); }}
            />
          </Tooltip>
          {record.orderStatus === "Ordered" ? (
            <Button 
              type="primary" 
              size="small" 
              icon={<UserAddOutlined />}
              onClick={() => { setSelectedOrderId(record._id); setShowAssignModal(true); }}
            >
              Assign
            </Button>
          ) : (
            <Button size="small" disabled>Assigned</Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      className="assign-orders-container"
    >
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <Title level={2} style={{ margin: 0 }}>Assign Orders</Title>
          <Text type="secondary">Manage and assign orders to delivery personnel</Text>
        </div>
      </div>

      <Card className="glass-card mb-4" bordered={false}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={8}>
            <Input
              placeholder="Search by Email, Address, Status or Tracking Number..."
              prefix={<SearchOutlined style={{ color: 'var(--text-muted)' }} />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="large"
              style={{ borderRadius: '12px' }}
            />
          </Col>
          <Col xs={12} md={4}>
            <Select
              placeholder="Filter by Status"
              style={{ width: '100%' }}
              size="large"
              value={statusFilter}
              onChange={setStatusFilter}
            >
              <Option value="">All Status</Option>
              <Option value="Ordered">Ordered</Option>
              <Option value="Ready to Ship">Ready to Ship</Option>
              <Option value="Out for Delivery">Out for Delivery</Option>
              <Option value="Delivered">Delivered</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
          </Col>
          <Col xs={12} md={4}>
            <Select
              placeholder="Assignment"
              style={{ width: '100%' }}
              size="large"
              value={assignmentFilter}
              onChange={setAssignmentFilter}
            >
              <Option value="">All Orders</Option>
              <Option value="Assigned">Assigned</Option>
              <Option value="Unassigned">Unassigned</Option>
            </Select>
          </Col>
          <Col xs={24} md={8}>
            <Select
              placeholder="Filter by Delivery Boy"
              style={{ width: '100%' }}
              size="large"
              value={assignedFilter}
              onChange={setAssignedFilter}
            >
              <Option value="">All Delivery Boys</Option>
              {deliveryBoys.map((boy) => (
                <Option key={boy._id} value={boy._id}>
                  {boy.firstname} {boy.lastname}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
      </Card>

      <Card className="glass-card" bordered={false} bodyStyle={{ padding: 0 }}>
        {loading ? (
          <div style={{ padding: '24px' }}>
            <Skeleton active />
            <Skeleton active />
          </div>
        ) : (
          <Table 
            columns={columns} 
            dataSource={filteredOrders} 
            rowKey="_id"
            pagination={{ 
              pageSize: 10,
              showTotal: (total) => `Total ${total} orders`,
              style: { padding: '16px' }
            }}
          />
        )}
      </Card>

      {/* Assign Modal */}
      <Modal
        title="Assign Delivery Boy"
        open={showAssignModal}
        onCancel={() => setShowAssignModal(false)}
        onOk={handleAssignOrder}
        confirmLoading={assigning}
        centered
        okText="Assign Now"
        okButtonProps={{ style: { borderRadius: '8px' } }}
        cancelButtonProps={{ style: { borderRadius: '8px' } }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Text>Choose a delivery personnel for this order:</Text>
          <Select
            placeholder="Search or select delivery boy..."
            style={{ width: '100%' }}
            size="large"
            showSearch
            optionFilterProp="children"
            value={selectedDeliveryBoy}
            onChange={setSelectedDeliveryBoy}
          >
            {deliveryBoys.map((boy) => (
              <Option key={boy._id} value={boy._id}>
                {boy.firstname} {boy.lastname} ({boy.email})
              </Option>
            ))}
          </Select>
        </Space>
      </Modal>

      {/* View Order Modal */}
      <Modal
        title={
          <Space>
            <InfoCircleOutlined style={{ color: 'var(--primary-color)' }} />
            <span>Order Details</span>
          </Space>
        }
        open={showViewModal}
        onCancel={() => setShowViewModal(false)}
        footer={[
          <Button key="close" onClick={() => setShowViewModal(false)} style={{ borderRadius: '8px' }}>
            Close
          </Button>
        ]}
        width={800}
        centered
      >
        <AnimatePresence>
          {viewOrder && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="order-details-sections">
                <Card size="small" className="mb-3" title="Customer Information" style={{ background: '#f8fafc' }}>
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Space><UserOutlined type="secondary" /><Text strong>Name:</Text> {viewOrder.shippingInfo?.firstName} {viewOrder.shippingInfo?.lastName}</Space>
                    </Col>
                    <Col span={12}>
                      <Space><MailOutlined style={{ color: '#4f46e5' }} /><Text strong>Email:</Text> {viewOrder.user?.email}</Space>
                    </Col>
                    <Col span={12}>
                      <Space><PhoneOutlined style={{ color: '#10b981' }} /><Text strong>Phone:</Text> {viewOrder.phoneNumber}</Space>
                    </Col>
                    <Col span={12}>
                      <Space><CreditCardOutlined style={{ color: '#8b5cf6' }} /><Text strong>Payment:</Text> {viewOrder.paymentInfo?.cashOndelivery ? "COD" : "Paid Online"}</Space>
                    </Col>
                    <Col span={24}>
                      <Space align="start"><EnvironmentOutlined style={{ color: '#ef4444' }} /><Text strong>Address:</Text> {viewOrder.shippingInfo?.address}, {viewOrder.shippingInfo?.city}, {viewOrder.shippingInfo?.state} - {viewOrder.shippingInfo?.pincode}</Space>
                    </Col>
                  </Row>
                </Card>

                <Card size="small" title="Order Items" style={{ background: '#f8fafc' }}>
                  <Table 
                    size="small"
                    pagination={false}
                    columns={[
                      {
                        title: "Image",
                        key: "image",
                        width: 80,
                        render: (record) => (
                          <div style={{ width: 40, height: 40, borderRadius: 6, overflow: 'hidden', border: '1px solid #eee' }}>
                            {record.product?.images?.[0]?.url ? (
                              <img 
                                src={`${BASEURL}${record.product.images[0].url}`} 
                                alt="product" 
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                              />
                            ) : (
                              <div style={{ width: '100%', height: '100%', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <InboxOutlined style={{ color: '#ccc' }} />
                              </div>
                            )}
                          </div>
                        )
                      },
                      { title: "Product", dataIndex: ["product", "title"], key: "name" },
                      { title: "Qty", dataIndex: "quantity", key: "qty" },
                      { title: "Price", dataIndex: "price", key: "price", render: (p) => `₹${p}` },
                      { title: "Total", key: "total", render: (r) => `₹${(r.price * r.quantity).toFixed(2)}` },
                    ]} 
                    dataSource={viewOrder.orderItems}
                    rowKey={(r) => r.product?._id || Math.random()}
                  />
                </Card>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Modal>

      <style jsx="true">{`
        .assign-orders-container {
          padding-bottom: 2rem;
        }
        .ant-table-thead > tr > th {
          background: #f8fafc !important;
          color: var(--text-muted) !important;
          font-weight: 600 !important;
          text-transform: uppercase;
          font-size: 12px;
          letter-spacing: 0.5px;
        }
        .order-details-sections .ant-card-head {
          border-bottom: 1px solid #e2e8f0;
          font-weight: 700;
        }
      `}</style>
    </motion.div>
  );
};

export default AssignOrders;

